import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getAllMetricInterval } from "@/service/reqUrl.service";

import type { AllMetricIntervalParams } from "./types";

interface UseAllMetricIntervalResult {
  /** Raw SP document (`{ MetricList: [...] }` or a bare array), kept unshaped —
   *  the view normalizes it. */
  data: unknown;
  loading: boolean;
  error: string | null;
}

/**
 * All Metrics Day/Week/Month interval grid, via POST /all-metric/interval
 * (`GetMetricValuesForDashboardInterval`). Re-fires when the selected date,
 * interval (DAY/WEEK/MONTH), service line, or any filter changes. `params` is
 * null until the service line resolves.
 *
 * NOTE for the caller: `AllMetricsInsight` guards against showing the previous
 * pivot's rows by comparing the returned `Interval` against the requested one
 * (`intervalStale`). That guard still works and is still needed — switching
 * pivot moves to a different cache entry, so `data` is null until the new one
 * resolves, and the check simply never trips on a cache hit.
 */
export function useAllMetricInterval(
  params: AllMetricIntervalParams | null,
): UseAllMetricIntervalResult {
  const userKey = useUserKey();

  const { data, loading, error } = useApiQuery<unknown>({
    queryKey: qk.interval(userKey, params),
    url: getAllMetricInterval,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch interval metrics",
  });

  return { data, loading, error };
}
